// src/global/countries.ts
import { siteOrigins } from '../utils/siteOrigins';
import { withTrailingSlash } from '../utils/trailingSlash';
/**
 * Canonical list of supported region codenames (lowercase).
 * Keep this in-sync with Kontent and your pageContext.availableCountries.
 */
export const supportedRegionCodes = [
    'gb-en',
    'my-en',
    'vn-en',
    'de-de',
    'au-en',
    'nz-en',
    'sg-en',
    'fr-fr',
] as const;

/**
 * IP country groups (two-letter ISO, lowercase)
 */
export const ukRegionIpCodes = [
    'gb',
    'se',
    'pt',
    'is',
    'es',
    'ie',
    'no',
    'dk',
    'ch',
    'fi',
    'sk',
    'it',
    'pl',
    'gr',
    'cy',
    'at',
    'hr',
    'ee',
    'lv',
    'lu',
    'nl',
    'ru',
    'ua',
    'rs',
    'md',
    'bg',
    'be',
    'cz',
    'hu',
    'ro',
    'lt',
    'si',
    'mt',
    'ba',
    'al',
    'me',
    'mk',
    'li',
] as const;

export const malaysianRegionIpCodes = [
    'th',
    'id',
    'ph',
    'hk',
    'tw',
    'in',
    'jp',
] as const;

/**
 * Domains keyed by region codename (lowercase).
 *
 * IMPORTANT: only regions that actually have distinct root hostnames
 * should be listed here (currently AU and NZ). Other regions intentionally
 * fall back to the default domain (https://www.sunlighten.com) and use
 * the language path (e.g. /my-en/..., /gb-en/..., etc).
 */
export const regionDomainUrls: Record<string, string> = siteOrigins;

/**
 * Region codes that need no `/{lang}/` path prefix — they are served
 * from their own dedicated domain root.
 * Includes 'default' (US), 'au-en', 'nz-en'.
 * NOTE: 'default' is intentionally included — it represents the US root site.
 * Never pass the literal string 'default' as a detectedRegionCode; use 'us-en'
 * for explicit US references.
 */
export const pathlessRegionCodes = Object.keys(regionDomainUrls).map((k) =>
    k.toLowerCase()
) as readonly string[];

const pathlessRegionSet = new Set(pathlessRegionCodes);

/** Friendly display names for regions (case-insensitive keys) */
export const regionFriendlyNames: Record<string, string> = {
    default: 'United States',
    global: 'United States',
    'us-en': 'United States',
    'my-en': 'Malaysia & Asia Pacific',
    'gb-en': 'UK & Europe English',
    'au-en': 'Australia',
    'nz-en': 'New Zealand',
    'de-de': 'Germany',
    'fr-fr': 'France',
    'sg-en': 'Singapore',
    'vn-en': 'Vietnam',
};

/** Return domain for a region code (case-insensitive). */
export function getDomainForCode(code?: string) {
    if (!code) return regionDomainUrls['default'];
    const key = String(code).toLowerCase();
    // only return separate host if explicitly defined in regionDomainUrls
    return regionDomainUrls[key] || regionDomainUrls['default'];
}

/** Splits a Sunlighten hostname into its environment prefix and TLD suffix. */
export function getSunlightenHostParts(host: string): {
    prefix: string;
    suffix: string;
} {
    const hostLower = host.toLowerCase();
    const splitIndex = hostLower.indexOf('sunlighten');
    return {
        prefix: host.slice(0, splitIndex),
        suffix: host.slice(splitIndex + 'sunlighten'.length),
    };
}

/** Extracts the TLD suffix (e.g. '.com', '.com.au', '.co.nz') from a regionDomainUrls entry. */
export function getSunlightenDomainSuffix(domainUrl: string): string {
    const hostname = new URL(domainUrl).hostname;
    const idx = hostname.indexOf('sunlighten');
    return hostname.slice(idx + 'sunlighten'.length);
}

/** Is this region a "root" domain (no language path)? */
export function isRootLang(code?: string) {
    if (!code) return false;
    return pathlessRegionSet.has(code.toLowerCase());
}

/** Returns the language path prefix for a region code, e.g. '/gb-en' or '' for pathless regions. */
export function getLangPrefix(regionCode?: string): string {
    if (!regionCode || isRootLang(regionCode)) return '';
    return '/' + regionCode.toLowerCase();
}

/**
 * Safely prepend the language prefix to a path, skipping when:
 * - the region is a root-lang (served from its own domain)
 * - the path already starts with the prefix
 * - the path is an absolute URL
 *
 * Also strips invalid pathless-region prefixes (e.g. `/au-en/`, `/nz-en/`)
 * since those regions are served from their own domains and should never
 * appear as path segments.
 */
export function prefixWithLang(path: string, regionCode?: string): string {
    if (!path) return path;
    if (
        /^[?#]/.test(path) ||
        /^[a-z][a-z0-9+.-]*:/i.test(path) ||
        path.startsWith('//')
    )
        return withTrailingSlash(path);
    if (!regionCode || isRootLang(regionCode)) return withTrailingSlash(path);

    const prefix = '/' + regionCode.toLowerCase();

    for (const code of supportedRegionCodes) {
        const existing = '/' + code;
        if (
            path.startsWith(existing + '/') ||
            path === existing ||
            path.startsWith(existing + '?') ||
            path.startsWith(existing + '#')
        ) {
            if (pathlessRegionSet.has(code)) {
                const stripped = path.slice(existing.length) || '/';
                return withTrailingSlash(
                    prefix + (stripped.startsWith('/') ? '' : '/') + stripped
                );
            }
            return withTrailingSlash(path);
        }
    }

    return withTrailingSlash(prefix + (path.startsWith('/') ? '' : '/') + path);
}

/**
 * Map an IP country (two-letter ISO) -> suggested region codename.
 * Rules:
 * - ukRegionIpCodes -> gb-en
 * - malaysianRegionIpCodes -> my-en
 * - direct mapping: match `${ipCode}-` in supportedRegionCodes
 * - fallback: 'default' (US)
 */
export function getSuggestedRegionFromIpCountry(
    ipCountryCode: string,
    preferredLanguage?: string
): string {
    const ipCode = (ipCountryCode || '').toLowerCase();

    if ((ukRegionIpCodes as readonly string[]).includes(ipCode)) return 'gb-en';

    if ((malaysianRegionIpCodes as readonly string[]).includes(ipCode))
        return 'my-en';

    const directMatch = (supportedRegionCodes as readonly string[]).find((c) =>
        c.startsWith(`${ipCode}-`)
    );
    if (directMatch) return directMatch;

    // fallback: suggest the default (US) region so visitors from unrecognized
    // countries see the banner when browsing any non-US regional site.
    return 'default';
}

/** Friendly display name for a region code (case-insensitive) */
export function getFriendlyRegionName(code?: string) {
    if (!code) return '';

    const regionKey = String(code).toLowerCase();

    if (regionFriendlyNames[regionKey]) {
        return regionFriendlyNames[regionKey];
    }

    const fallback = regionKey.split('-')[0];
    return fallback.charAt(0).toUpperCase() + fallback.slice(1);
}

export const allCountryPhoneNumberCodes = {
    Afghanistan: { phoneCode: '+93', countryCode: 'AF' },
    Albania: { phoneCode: '+355', countryCode: 'AL' },
    Algeria: { phoneCode: '+213', countryCode: 'DZ' },
    'American Samoa': { phoneCode: '+1', countryCode: 'AS' },
    Andorra: { phoneCode: '+376', countryCode: 'AD' },
    Angola: { phoneCode: '+244', countryCode: 'AO' },
    Anguilla: { phoneCode: '+1', countryCode: 'AI' },
    'Antigua and Barbuda': { phoneCode: '+1', countryCode: 'AG' },
    Argentina: { phoneCode: '+54', countryCode: 'AR' },
    Armenia: { phoneCode: '+374', countryCode: 'AM' },
    Aruba: { phoneCode: '+297', countryCode: 'AW' },
    Australia: { phoneCode: '+61', countryCode: 'AU' },
    Austria: { phoneCode: '+43', countryCode: 'AT' },
    Azerbaijan: { phoneCode: '+994', countryCode: 'AZ' },
    Bahamas: { phoneCode: '+1', countryCode: 'BS' },
    Bahrain: { phoneCode: '+973', countryCode: 'BH' },
    Bangladesh: { phoneCode: '+880', countryCode: 'BD' },
    Barbados: { phoneCode: '+1', countryCode: 'BB' },
    Belarus: { phoneCode: '+375', countryCode: 'BY' },
    Belgium: { phoneCode: '+32', countryCode: 'BE' },
    Belize: { phoneCode: '+501', countryCode: 'BZ' },
    Benin: { phoneCode: '+229', countryCode: 'BJ' },
    Bermuda: { phoneCode: '+1', countryCode: 'BM' },
    Bhutan: { phoneCode: '+975', countryCode: 'BT' },
    Bolivia: { phoneCode: '+591', countryCode: 'BO' },
    'Bosnia and Herzegovina': { phoneCode: '+387', countryCode: 'BA' },
    Botswana: { phoneCode: '+267', countryCode: 'BW' },
    Brazil: { phoneCode: '+55', countryCode: 'BR' },
    'British Indian Ocean Territory': { phoneCode: '+246', countryCode: 'IO' },
    'British Virgin Islands': { phoneCode: '+1', countryCode: 'VG' },
    Brunei: { phoneCode: '+673', countryCode: 'BN' },
    Bulgaria: { phoneCode: '+359', countryCode: 'BG' },
    'Burkina Faso': { phoneCode: '+226', countryCode: 'BF' },
    Burundi: { phoneCode: '+257', countryCode: 'BI' },
    Cambodia: { phoneCode: '+855', countryCode: 'KH' },
    Cameroon: { phoneCode: '+237', countryCode: 'CM' },
    Canada: { phoneCode: '+1', countryCode: 'CA' },
    'Cape Verde': { phoneCode: '+238', countryCode: 'CV' },
    'Cayman Islands': { phoneCode: '+1', countryCode: 'KY' },
    'Central African Republic': { phoneCode: '+236', countryCode: 'CF' },
    Chad: { phoneCode: '+235', countryCode: 'TD' },
    Chile: { phoneCode: '+56', countryCode: 'CL' },
    China: { phoneCode: '+86', countryCode: 'CN' },
    'Christmas Island': { phoneCode: '+61', countryCode: 'CX' },
    'Cocos Islands': { phoneCode: '+61', countryCode: 'CC' },
    Colombia: { phoneCode: '+57', countryCode: 'CO' },
    Comoros: { phoneCode: '+269', countryCode: 'KM' },
    'Cook Islands': { phoneCode: '+682', countryCode: 'CK' },
    'Costa Rica': { phoneCode: '+506', countryCode: 'CR' },
    Croatia: { phoneCode: '+385', countryCode: 'HR' },
    Cuba: { phoneCode: '+53', countryCode: 'CU' },
    Curacao: { phoneCode: '+599', countryCode: 'CW' },
    Cyprus: { phoneCode: '+357', countryCode: 'CY' },
    'Czech Republic': { phoneCode: '+420', countryCode: 'CZ' },
    'Democratic Republic of the Congo': {
        phoneCode: '+243',
        countryCode: 'CD',
    },
    Denmark: { phoneCode: '+45', countryCode: 'DK' },
    Djibouti: { phoneCode: '+253', countryCode: 'DJ' },
    Dominica: { phoneCode: '+1', countryCode: 'DM' },
    'Dominican Republic': { phoneCode: '+1', countryCode: 'DO' },
    'East Timor': { phoneCode: '+670', countryCode: 'TL' },
    Ecuador: { phoneCode: '+593', countryCode: 'EC' },
    Egypt: { phoneCode: '+20', countryCode: 'EG' },
    'El Salvador': { phoneCode: '+503', countryCode: 'SV' },
    'Equatorial Guinea': { phoneCode: '+240', countryCode: 'GQ' },
    Eritrea: { phoneCode: '+291', countryCode: 'ER' },
    Estonia: { phoneCode: '+372', countryCode: 'EE' },
    Eswatini: { phoneCode: '+268', countryCode: 'SZ' },
    Ethiopia: { phoneCode: '+251', countryCode: 'ET' },
    'Falkland Islands': { phoneCode: '+500', countryCode: 'FK' },
    'Faroe Islands': { phoneCode: '+298', countryCode: 'FO' },
    Fiji: { phoneCode: '+679', countryCode: 'FJ' },
    Finland: { phoneCode: '+358', countryCode: 'FI' },
    France: { phoneCode: '+33', countryCode: 'FR' },
    'French Guiana': { phoneCode: '+594', countryCode: 'GF' },
    'French Polynesia': { phoneCode: '+689', countryCode: 'PF' },
    'French Southern Territories': { phoneCode: '+262', countryCode: 'TF' },
    Gabon: { phoneCode: '+241', countryCode: 'GA' },
    Gambia: { phoneCode: '+220', countryCode: 'GM' },
    Georgia: { phoneCode: '+995', countryCode: 'GE' },
    Germany: { phoneCode: '+49', countryCode: 'DE' },
    Ghana: { phoneCode: '+233', countryCode: 'GH' },
    Gibraltar: { phoneCode: '+350', countryCode: 'GI' },
    Greece: { phoneCode: '+30', countryCode: 'GR' },
    Greenland: { phoneCode: '+299', countryCode: 'GL' },
    Grenada: { phoneCode: '+1', countryCode: 'GD' },
    Guadeloupe: { phoneCode: '+590', countryCode: 'GP' },
    Guam: { phoneCode: '+1', countryCode: 'GU' },
    Guatemala: { phoneCode: '+502', countryCode: 'GT' },
    Guernsey: { phoneCode: '+44', countryCode: 'GG' },
    Guinea: { phoneCode: '+224', countryCode: 'GN' },
    'Guinea-Bissau': { phoneCode: '+245', countryCode: 'GW' },
    Guyana: { phoneCode: '+592', countryCode: 'GY' },
    Haiti: { phoneCode: '+509', countryCode: 'HT' },
    'Heard Island and McDonald Islands': {
        phoneCode: '+672',
        countryCode: 'HM',
    },
    Honduras: { phoneCode: '+504', countryCode: 'HN' },
    'Hong Kong': { phoneCode: '+852', countryCode: 'HK' },
    Hungary: { phoneCode: '+36', countryCode: 'HU' },
    Iceland: { phoneCode: '+354', countryCode: 'IS' },
    India: { phoneCode: '+91', countryCode: 'IN' },
    Indonesia: { phoneCode: '+62', countryCode: 'ID' },
    Iran: { phoneCode: '+98', countryCode: 'IR' },
    Iraq: { phoneCode: '+964', countryCode: 'IQ' },
    Ireland: { phoneCode: '+353', countryCode: 'IE' },
    'Isle of Man': { phoneCode: '+44', countryCode: 'IM' },
    Israel: { phoneCode: '+972', countryCode: 'IL' },
    Italy: { phoneCode: '+39', countryCode: 'IT' },
    'Ivory Coast': { phoneCode: '+225', countryCode: 'CI' },
    Jamaica: { phoneCode: '+1', countryCode: 'JM' },
    Japan: { phoneCode: '+81', countryCode: 'JP' },
    Jersey: { phoneCode: '+44', countryCode: 'JE' },
    Jordan: { phoneCode: '+962', countryCode: 'JO' },
    Kazakhstan: { phoneCode: '+7', countryCode: 'KZ' },
    Kenya: { phoneCode: '+254', countryCode: 'KE' },
    Kiribati: { phoneCode: '+686', countryCode: 'KI' },
    Kosovo: { phoneCode: '+383', countryCode: 'XK' },
    Kuwait: { phoneCode: '+965', countryCode: 'KW' },
    Kyrgyzstan: { phoneCode: '+996', countryCode: 'KG' },
    Laos: { phoneCode: '+856', countryCode: 'LA' },
    Latvia: { phoneCode: '+371', countryCode: 'LV' },
    Lebanon: { phoneCode: '+961', countryCode: 'LB' },
    Lesotho: { phoneCode: '+266', countryCode: 'LS' },
    Liberia: { phoneCode: '+231', countryCode: 'LR' },
    Libya: { phoneCode: '+218', countryCode: 'LY' },
    Liechtenstein: { phoneCode: '+423', countryCode: 'LI' },
    Lithuania: { phoneCode: '+370', countryCode: 'LT' },
    Luxembourg: { phoneCode: '+352', countryCode: 'LU' },
    Macao: { phoneCode: '+853', countryCode: 'MO' },
    Macedonia: { phoneCode: '+389', countryCode: 'MK' },
    Madagascar: { phoneCode: '+261', countryCode: 'MG' },
    Malawi: { phoneCode: '+265', countryCode: 'MW' },
    Malaysia: { phoneCode: '+60', countryCode: 'MY' },
    Maldives: { phoneCode: '+960', countryCode: 'MV' },
    Mali: { phoneCode: '+223', countryCode: 'ML' },
    Malta: { phoneCode: '+356', countryCode: 'MT' },
    'Marshall Islands': { phoneCode: '+692', countryCode: 'MH' },
    Martinique: { phoneCode: '+596', countryCode: 'MQ' },
    Mauritania: { phoneCode: '+222', countryCode: 'MR' },
    Mauritius: { phoneCode: '+230', countryCode: 'MU' },
    Mayotte: { phoneCode: '+262', countryCode: 'YT' },
    Mexico: { phoneCode: '+52', countryCode: 'MX' },
    Micronesia: { phoneCode: '+691', countryCode: 'FM' },
    Moldova: { phoneCode: '+373', countryCode: 'MD' },
    Monaco: { phoneCode: '+377', countryCode: 'MC' },
    Mongolia: { phoneCode: '+976', countryCode: 'MN' },
    Montenegro: { phoneCode: '+382', countryCode: 'ME' },
    Montserrat: { phoneCode: '+1', countryCode: 'MS' },
    Morocco: { phoneCode: '+212', countryCode: 'MA' },
    Mozambique: { phoneCode: '+258', countryCode: 'MZ' },
    Myanmar: { phoneCode: '+95', countryCode: 'MM' },
    Namibia: { phoneCode: '+264', countryCode: 'NA' },
    Nauru: { phoneCode: '+674', countryCode: 'NR' },
    Nepal: { phoneCode: '+977', countryCode: 'NP' },
    Netherlands: { phoneCode: '+31', countryCode: 'NL' },
    'New Caledonia': { phoneCode: '+687', countryCode: 'NC' },
    'New Zealand': { phoneCode: '+64', countryCode: 'NZ' },
    Nicaragua: { phoneCode: '+505', countryCode: 'NI' },
    Niger: { phoneCode: '+227', countryCode: 'NE' },
    Nigeria: { phoneCode: '+234', countryCode: 'NG' },
    Niue: { phoneCode: '+683', countryCode: 'NU' },
    'Norfolk Island': { phoneCode: '+672', countryCode: 'NF' },
    'North Korea': { phoneCode: '+850', countryCode: 'KP' },
    'Northern Mariana Islands': { phoneCode: '+1', countryCode: 'MP' },
    Norway: { phoneCode: '+47', countryCode: 'NO' },
    Oman: { phoneCode: '+968', countryCode: 'OM' },
    Pakistan: { phoneCode: '+92', countryCode: 'PK' },
    Palau: { phoneCode: '+680', countryCode: 'PW' },
    'Palestinian Territory': { phoneCode: '+970', countryCode: 'PS' },
    Panama: { phoneCode: '+507', countryCode: 'PA' },
    'Papua New Guinea': { phoneCode: '+675', countryCode: 'PG' },
    Paraguay: { phoneCode: '+595', countryCode: 'PY' },
    Peru: { phoneCode: '+51', countryCode: 'PE' },
    Philippines: { phoneCode: '+63', countryCode: 'PH' },
    Pitcairn: { phoneCode: '+870', countryCode: 'PN' },
    Poland: { phoneCode: '+48', countryCode: 'PL' },
    Portugal: { phoneCode: '+351', countryCode: 'PT' },
    'Puerto Rico': { phoneCode: '+1', countryCode: 'PR' },
    Qatar: { phoneCode: '+974', countryCode: 'QA' },
    Reunion: { phoneCode: '+262', countryCode: 'RE' },
    Romania: { phoneCode: '+40', countryCode: 'RO' },
    Russia: { phoneCode: '+7', countryCode: 'RU' },
    Rwanda: { phoneCode: '+250', countryCode: 'RW' },
    'Saint Barthelemy': { phoneCode: '+590', countryCode: 'BL' },
    'Saint Helena': { phoneCode: '+290', countryCode: 'SH' },
    'Saint Kitts and Nevis': { phoneCode: '+1', countryCode: 'KN' },
    'Saint Lucia': { phoneCode: '+1', countryCode: 'LC' },
    'Saint Martin': { phoneCode: '+590', countryCode: 'MF' },
    'Saint Pierre and Miquelon': { phoneCode: '+508', countryCode: 'PM' },
    'Saint Vincent and the Grenadines': { phoneCode: '+1', countryCode: 'VC' },
    Samoa: { phoneCode: '+685', countryCode: 'WS' },
    'San Marino': { phoneCode: '+378', countryCode: 'SM' },
    'Sao Tome and Principe': { phoneCode: '+239', countryCode: 'ST' },
    'Saudi Arabia': { phoneCode: '+966', countryCode: 'SA' },
    Senegal: { phoneCode: '+221', countryCode: 'SN' },
    Serbia: { phoneCode: '+381', countryCode: 'RS' },
    Seychelles: { phoneCode: '+248', countryCode: 'SC' },
    'Sierra Leone': { phoneCode: '+232', countryCode: 'SL' },
    Singapore: { phoneCode: '+65', countryCode: 'SG' },
    'Sint Maarten': { phoneCode: '+599', countryCode: 'SX' },
    Slovakia: { phoneCode: '+421', countryCode: 'SK' },
    Slovenia: { phoneCode: '+386', countryCode: 'SI' },
    'Solomon Islands': { phoneCode: '+677', countryCode: 'SB' },
    Somalia: { phoneCode: '+252', countryCode: 'SO' },
    'South Africa': { phoneCode: '+27', countryCode: 'ZA' },
    'South Georgia and the South Sandwich Islands': {
        phoneCode: '+500',
        countryCode: 'GS',
    },
    'South Korea': { phoneCode: '+82', countryCode: 'KR' },
    'South Sudan': { phoneCode: '+211', countryCode: 'SS' },
    Spain: { phoneCode: '+34', countryCode: 'ES' },
    'Sri Lanka': { phoneCode: '+94', countryCode: 'LK' },
    Sudan: { phoneCode: '+249', countryCode: 'SD' },
    Suriname: { phoneCode: '+597', countryCode: 'SR' },
    Swaziland: { phoneCode: '+268', countryCode: 'SZ' },
    Sweden: { phoneCode: '+46', countryCode: 'SE' },
    Switzerland: { phoneCode: '+41', countryCode: 'CH' },
    Syria: { phoneCode: '+963', countryCode: 'SY' },
    'Svalbard and Jan Mayen': { phoneCode: '+47', countryCode: 'SJ' },
    Taiwan: { phoneCode: '+886', countryCode: 'TW' },
    Tajikistan: { phoneCode: '+992', countryCode: 'TJ' },
    Tanzania: { phoneCode: '+255', countryCode: 'TZ' },
    Thailand: { phoneCode: '+66', countryCode: 'TH' },
    Togo: { phoneCode: '+228', countryCode: 'TG' },
    Tokelau: { phoneCode: '+690', countryCode: 'TK' },
    Tonga: { phoneCode: '+676', countryCode: 'TO' },
    'Trinidad and Tobago': { phoneCode: '+1', countryCode: 'TT' },
    Tunisia: { phoneCode: '+216', countryCode: 'TN' },
    Turkey: { phoneCode: '+90', countryCode: 'TR' },
    Turkmenistan: { phoneCode: '+993', countryCode: 'TM' },
    'Turks and Caicos Islands': { phoneCode: '+1', countryCode: 'TC' },
    Tuvalu: { phoneCode: '+688', countryCode: 'TV' },
    Uganda: { phoneCode: '+256', countryCode: 'UG' },
    Ukraine: { phoneCode: '+380', countryCode: 'UA' },
    'United Arab Emirates': { phoneCode: '+971', countryCode: 'AE' },
    'United Kingdom': { phoneCode: '+44', countryCode: 'GB' },
    'United States': { phoneCode: '+1', countryCode: 'US' },
    'United States Minor Outlying Islands': {
        phoneCode: '+1',
        countryCode: 'UM',
    },
    Uruguay: { phoneCode: '+598', countryCode: 'UY' },
    Uzbekistan: { phoneCode: '+998', countryCode: 'UZ' },
    Vanuatu: { phoneCode: '+678', countryCode: 'VU' },
    Vatican: { phoneCode: '+379', countryCode: 'VA' },
    Venezuela: { phoneCode: '+58', countryCode: 'VE' },
    Vietnam: { phoneCode: '+84', countryCode: 'VN' },
    'Wallis and Futuna': { phoneCode: '+681', countryCode: 'WF' },
    'Western Sahara': { phoneCode: '+212', countryCode: 'EH' },
    Yemen: { phoneCode: '+967', countryCode: 'YE' },
    Zambia: { phoneCode: '+260', countryCode: 'ZM' },
    Zimbabwe: { phoneCode: '+263', countryCode: 'ZW' },
};
